import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Button, Grid, Alert,
  CircularProgress, Dialog, DialogTitle,
  DialogContent, DialogActions, Paper, Typography,
  TextField, IconButton, Autocomplete, Avatar, Tooltip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, InputAdornment, MenuItem
} from '@mui/material';
import { Add, Save, Delete as DeleteIcon, CameraAlt, LocationOn, MyLocation, PhoneAndroid, DesktopWindows, Block } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

import DynamicTable from '../Components/Dynamic/DynamicTable';
import DynamicOffcanvas from '../Components/Dynamic/DynamicOffcanvas';
import DynamicField from '../Components/Dynamic/DynamicField';

import { useUser } from '../Context/UserContext';
import API_URLS from '../Services/api/action';

// ✅✅✅ STRICT REAL MOBILE DEVICE DETECTION ✅✅✅
// This ONLY checks ACTUAL hardware/device identity via User Agent
// This will NEVER be affected by:
// - Window resizing
// - Browser zoom
// - Responsive Design Mode in DevTools
// - Screen orientation changes
// - Any CSS media queries
const detectRealMobileDevice = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera || '';
  
  // Strict mobile device patterns - these ONLY match real mobile devices
  const isRealMobile = 
    /Android(\s+\d+\.\d+)?;/i.test(userAgent) ||      // Android phone/tablet
    /iPhone/i.test(userAgent) ||                        // iPhone
    /iPad/i.test(userAgent) ||                          // iPad
    /iPod/i.test(userAgent) ||                          // iPod Touch
    /webOS/i.test(userAgent) ||                         // Palm/webOS
    /BlackBerry/i.test(userAgent) ||                    // BlackBerry
    /IEMobile/i.test(userAgent) ||                      // Windows Phone
    /Opera Mini/i.test(userAgent) ||                    // Opera Mini (mobile only)
    /Mobile\s+Safari/i.test(userAgent) ||               // Mobile Safari
    /Windows\s+Phone/i.test(userAgent) ||               // Windows Phone
    /Nokia/i.test(userAgent) ||                         // Nokia
    /Samsung/i.test(userAgent) ||                       // Samsung
    /LG/i.test(userAgent) && /Mobile/i.test(userAgent) || // LG Mobile
    /Sony\s+Ericsson/i.test(userAgent) ||               // Sony Ericsson
    /Motorola/i.test(userAgent) && /Mobile/i.test(userAgent); // Motorola Mobile
  
  // Extra safety: Block if we detect desktop browsers
  // These will NEVER appear in mobile device User Agents
  const isDesktopBrowser = 
    /Windows\s+NT/i.test(userAgent) && !/Windows\s+Phone/i.test(userAgent) ||  // Windows Desktop
    /Macintosh/i.test(userAgent) && !/Mobile/i.test(userAgent) ||               // macOS Desktop
    /Linux\s+x86_64/i.test(userAgent) ||                                          // Linux Desktop
    /X11/i.test(userAgent) && !/Android/i.test(userAgent);                       // Unix/Linux Desktop
  
  // If desktop browser detected, ALWAYS return false
  if (isDesktopBrowser) {
    return false;
  }
  
  return isRealMobile;
};

// ✅ ONE-TIME CHECK - This value is computed ONCE and NEVER changes
// Stored in a module-level variable so it persists across re-renders
const IS_REAL_MOBILE_DEVICE = detectRealMobileDevice();

// Log for debugging (remove in production)
console.log('🔒 Device Detection Result:', {
  isMobile: IS_REAL_MOBILE_DEVICE,
  userAgent: navigator.userAgent,
  platform: navigator.platform,
  timestamp: new Date().toISOString()
});

const tableStyles = `
  .species-stock-table .MuiTableCell-root {
    white-space: nowrap !important;
    padding: 10px 16px !important;
    font-size: 13px !important;
  }
  .species-stock-table .MuiTableCell-head {
    white-space: nowrap !important;
    font-weight: 600 !important;
    background-color: #0a6831 !important;
    color: #ffffff !important;
    border-bottom: 2px solid #085a2a !important;
  }
  .species-stock-table .MuiTableCell-head span {
    color: #ffffff !important;
  }
  .species-stock-table .MuiTable-root {
    min-width: 1200px;
    width: 100%;
  }
  .species-stock-table .address-cell {
    white-space: normal !important;
    display: -webkit-box !important;
    -webkit-line-clamp: 2 !important;
    -webkit-box-orient: vertical !important;
    overflow: hidden !important;
    max-width: 200px !important;
    min-width: 150px !important;
  }
  .species-stock-table .image-cell {
    white-space: nowrap !important;
  }
  .species-stock-table .MuiTableRow-head:hover {
    background-color: #0a6831 !important;
  }
`;

const SpeciesStock = () => {
  const { productionCenterId, token } = useUser();
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [speciesOptions, setSpeciesOptions] = useState([]);
  const [open, setOpen] = useState(false);

  // ✅ USE THE MODULE-LEVEL CONSTANT - This can NEVER change
  const isMobile = IS_REAL_MOBILE_DEVICE;

  const greenInputSx = {
    width:"100%",
    '& .MuiOutlinedInput-root': {
      '& .MuiOutlinedInput-notchedOutline': { borderColor: '#4caf50', borderWidth: 1.5 },
      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#2e7d32 !important', borderWidth: 2 },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#1b5e20', borderWidth: 2 },
    },
    '& .MuiInputLabel-root': { color: '#000000', fontWeight: 500 },
    '& .MuiInputLabel-root.Mui-focused': { color: '#000000' },
  };

  const getInitialItemState = () => ({
    id: Date.now(), species_id: '', species_name: '', saplings_available: '', price_per_sapling: '',
    production_date: null, sapling_age: '', sapling_age_count: '', sapling_age_unit: 'Weeks',
    allocated_quantity: 0, image_file: null, image_preview: null,
    latitude: '', longitude: '', address: '', is_fetching_location: false
  });

  const [items, setItems] = useState([getInitialItemState()]);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [allocationModalOpen, setAllocationModalOpen] = useState(false);
  const [allocationData, setAllocationData] = useState([]);
  const [allocationLoading, setAllocationLoading] = useState(false);
  const [selectedStockInfo, setSelectedStockInfo] = useState(null);
  const [desktopBlockAlert, setDesktopBlockAlert] = useState(false);

  const getHeaders = () => ({ Authorization: `Bearer ${token}` });
  const normalize = (res) => Array.isArray(res) ? res : res?.results || res?.data || [];

  const fetchSpeciesOptions = async () => {
    try {
      const res = await fetch('/api/center/trees', { headers: getHeaders() });
      const data = await res.json();
      setSpeciesOptions(Array.isArray(data) ? data : data?.results || data?.data || []);
    } catch (err) { console.error("Error fetching species options:", err); }
  };

  const fetchData = async () => {
    if (!productionCenterId) return;
    setLoading(true);
    try {
      const res = await fetch(`/api/${API_URLS.species}?production_center_id=${productionCenterId}`, { headers: getHeaders() });
      const speciesData = normalize(await res.json());
      const finalData = speciesData.map(species => {
        const total = Number(species.saplings_available) || 0;
        const allocated = Number(species.allocated_quantity) || 0;
        return { ...species, in_hand: total - allocated, image: species.image || null, total_selled: species.total_selled || 0, total_selled_price: species.total_selled_price || 0, address: species.address || 'N/A' };
      });
      setTableData(finalData);
    } catch (err) { console.error("Fetch error:", err); }
    finally { setLoading(false); setInitialLoading(false); }
  };

  useEffect(() => { if (token) fetchSpeciesOptions(); }, [token]);
  useEffect(() => { fetchData(); }, [productionCenterId]);

  useEffect(() => {
    if (open && !editMode) {
      // ✅ STRICT BLOCK: Double-check even when opening
      if (!IS_REAL_MOBILE_DEVICE) {
        setDesktopBlockAlert(true);
        setOpen(false);
        return;
      }
      const newItem = getInitialItemState();
      newItem.is_fetching_location = true;
      setItems([newItem]);
      setErrors({});
      setTimeout(() => fetchLocationAndAddress(0), 300);
    }
  }, [open, editMode]);

  // ✅ STRICT BLOCK: Block resize attempts to trick the system
  useEffect(() => {
    const handleResize = () => {
      // This effect intentionally does NOTHING
      // isMobile is a CONSTANT that never changes
      // Window resize has NO effect on device detection
    };
    
    // Listen to resize just to prove we ignore it
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleAllocatedClick = async (row) => {
    setSelectedStockInfo(row); setAllocationModalOpen(true); setAllocationLoading(true); setAllocationData([]);
    try {
      const res = await fetch(`/api/users/farmer-request-items/?stock_id=${row.id}`, { headers: getHeaders() });
      const data = await res.json();
      setAllocationData(Array.isArray(data) ? data : []);
    } catch (err) { console.error("Error fetching allocation details:", err); }
    finally { setAllocationLoading(false); }
  };

  const validate = () => {
    let newErrors = {};
    items.forEach((item, index) => {
      newErrors[index] = {};
      if (!editMode && !item.species_id) newErrors[index].species_id = "Species Required";
      if (!item.saplings_available) newErrors[index].saplings_available = "Quantity Required";
      if (!item.price_per_sapling) newErrors[index].price_per_sapling = "Price Required";
      if (!editMode) {
        if (!item.production_date) newErrors[index].production_date = "Required";
        if (!item.image_file) newErrors[index].image_file = "Image Capture is Required";
      }
    });
    setErrors(newErrors);
    return !items.some((_, i) => Object.values(newErrors[i]).some(Boolean));
  };

  const handleChange = (i, e) => {
    const updated = [...items]; updated[i][e.target.name] = e.target.value; setItems(updated);
    if (errors[i] && errors[i][e.target.name]) setErrors(prev => ({ ...prev, [i]: { ...prev[i], [e.target.name]: '' } }));
  };

  const handleDateChange = (i, newValue) => {
    const updated = [...items]; updated[i].production_date = newValue; setItems(updated);
    if (errors[i]?.production_date) setErrors(prev => ({ ...prev, [i]: { ...prev[i], production_date: '' } }));
  };

  const handleSpeciesChange = (i, newValue) => {
    const updated = [...items];
    if (newValue) updated[i] = { ...updated[i], species_id: newValue.id, species_name: newValue.species_name || newValue.name || '' };
    else updated[i] = { ...updated[i], species_id: '', species_name: '' };
    setItems(updated);
    if (errors[i]?.species_id) setErrors(prev => ({ ...prev, [i]: { ...prev[i], species_id: '' } }));
  };

  const burnLocationIntoImage = (index, lat, lng) => {
    setItems(prev => {
      const item = prev[index];
      if (!item || !item.image_preview) return prev;
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas'); canvas.width = img.width; canvas.height = img.height;
        const ctx = canvas.getContext('2d'); ctx.drawImage(img, 0, 0);
        const text = `Lat: ${lat.toFixed(5)}, Lng: ${lng.toFixed(5)}`;
        const fontSize = Math.max(16, Math.floor(canvas.width / 20)); ctx.font = `bold ${fontSize}px Arial`;
        const textMetrics = ctx.measureText(text); const padding = 10;
        const textHeight = fontSize + (padding * 2); const textWidth = textMetrics.width + (padding * 2);
        ctx.fillStyle = 'rgba(0, 0, 0, 0.6)'; ctx.fillRect(0, canvas.height - textHeight, textWidth, textHeight);
        ctx.fillStyle = 'white'; ctx.textBaseline = 'middle'; ctx.fillText(text, padding, canvas.height - (textHeight / 2));
        canvas.toBlob((blob) => {
          if (blob) {
            const modifiedFile = new File([blob], `geo_tagged_${Date.now()}.jpg`, { type: 'image/jpeg' });
            setItems(prevItems => { const u = [...prevItems]; u[index] = { ...u[index], image_file: modifiedFile, image_preview: canvas.toDataURL('image/jpeg') }; return u; });
          }
        }, 'image/jpeg', 0.9);
      };
      img.src = item.image_preview;
      return prev;
    });
  };

  const fetchLocationAndAddress = (index) => {
    // ✅ STRICT BLOCK: Don't even try to get location on desktop
    if (!IS_REAL_MOBILE_DEVICE) {
      setItems(p => { const u = [...p]; if (u[index]) { u[index].is_fetching_location = false; u[index].address = "Mobile device required"; } return u; });
      return;
    }
    
    if (!navigator.geolocation) { 
      alert("Geolocation not supported."); 
      setItems(p => { const u = [...p]; if (u[index]) u[index].is_fetching_location = false; return u; }); 
      return; 
    }
    setItems(p => { const u = [...p]; if (u[index]) u[index].is_fetching_location = true; return u; });
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = pos.coords.latitude; const lng = pos.coords.longitude;
        setItems(p => { const u = [...p]; if (u[index]) { u[index].latitude = lat; u[index].longitude = lng; } return u; });
        burnLocationIntoImage(index, lat, lng);
        try {
          const res = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`);
          const data = await res.json();
          setItems(p => { const u = [...p]; if (u[index]) { u[index].address = data.display_name || `Lat: ${lat.toFixed(4)}`; u[index].is_fetching_location = false; } return u; });
        } catch (err) { setItems(p => { const u = [...p]; if (u[index]) { u[index].address = "Address not found"; u[index].is_fetching_location = false; } return u; }); }
      },
      (error) => { alert("Unable to fetch location."); setItems(p => { const u = [...p]; if (u[index]) { u[index].is_fetching_location = false; u[index].address = "Permission Denied"; } return u; }); },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  };

  const handleImageCapture = (index, event) => {
    // ✅ STRICT BLOCK: Block image capture on desktop
    if (!IS_REAL_MOBILE_DEVICE) {
      alert("Image capture is only available on mobile devices.");
      event.target.value = '';
      return;
    }
    
    const file = event.target.files[0]; if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      setItems(p => { const u = [...p]; if (u[index]) { u[index].image_preview = reader.result; u[index].image_file = file; } return u; });
      fetchLocationAndAddress(index);
    };
    reader.readAsDataURL(file);
  };

  const addRow = () => { 
    // ✅ STRICT BLOCK: Prevent adding rows on desktop
    if (!IS_REAL_MOBILE_DEVICE) {
      setDesktopBlockAlert(true);
      return;
    }
    setItems(p => [...p, getInitialItemState()]); 
    setTimeout(() => fetchLocationAndAddress(items.length), 100); 
  };
  
  const removeRow = (i) => { if (items.length === 1) return; setItems(items.filter((_, idx) => idx !== i)); };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      if (editMode) {
        const formData = new FormData();
        if (items[0].species_id) formData.append('species_id', Number(items[0].species_id));
        formData.append('saplings_available', Number(items[0].saplings_available));
        const combinedAge = items[0].sapling_age_count && items[0].sapling_age_unit ? `${items[0].sapling_age_count} ${items[0].sapling_age_unit}` : '';
        formData.append('sapling_age', combinedAge);
        formData.append('price_per_sapling', Number(items[0].price_per_sapling));
        if (items[0].production_date) formData.append('production_date', new Date(items[0].production_date).toISOString().split('T')[0]);
        if (items[0].latitude) formData.append('latitude', items[0].latitude);
        if (items[0].longitude) formData.append('longitude', items[0].longitude);
        if (items[0].address) formData.append('address', items[0].address);
        if (items[0].image_file instanceof File) formData.append('image', items[0].image_file);
        await fetch(`/api/${API_URLS.species}?id=${editingId}`, { method: 'PUT', headers: getHeaders(), body: formData });
      } else {
        const formData = new FormData();
        items.forEach((item, index) => {
          formData.append(`items[${index}][production_center_id]`, productionCenterId);
          formData.append(`items[${index}][species_id]`, Number(item.species_id));
          formData.append(`items[${index}][saplings_available]`, Number(item.saplings_available));
          formData.append(`items[${index}][price_per_sapling]`, Number(item.price_per_sapling));
          formData.append(`items[${index}][production_date]`, item.production_date ? new Date(item.production_date).toISOString().split('T')[0] : '');
          const combinedAge = item.sapling_age_count && item.sapling_age_unit ? `${item.sapling_age_count} ${item.sapling_age_unit}` : '';
          formData.append(`items[${index}][sapling_age]`, combinedAge);
          if (item.image_file) formData.append(`items[${index}][image]`, item.image_file);
          formData.append(`items[${index}][latitude]`, item.latitude);
          formData.append(`items[${index}][longitude]`, item.longitude);
          formData.append(`items[${index}][address]`, item.address);
        });
        await fetch(`/api/${API_URLS.species}`, { method: 'POST', headers: getHeaders(), body: formData });
      }
      setOpen(false); setEditMode(false); fetchData();
    } catch (err) { console.error("Submit error:", err); }
    finally { setSubmitting(false); }
  };

  const handleDeleteClick = (row) => { setSelectedRow(row); setDeleteOpen(true); };
  const confirmDelete = async () => {
    if (!selectedRow) return;
    try { await fetch(`/api/${API_URLS.species}?id=${selectedRow.id}`, { method: 'DELETE', headers: getHeaders() }); } catch (err) { console.error(err); }
    setDeleteOpen(false); setSelectedRow(null); fetchData();
  };

  const handleEdit = (row) => {
    setEditMode(true);
    setEditingId(row.id);
    const imageUrl = row.image ? `/api/${row.image.replace(/\\/g, '/')}` : null;
    const ageParts = (row.sapling_age || '').split(' ');
    setItems([{
      id: row.id, species_id: row.species_id || '', species_name: row.species_name || '',
      saplings_available: row.saplings_available || '', price_per_sapling: row.price_per_sapling || '',
      allocated_quantity: row.allocated_quantity || 0, sapling_age: row.sapling_age || '',
      sapling_age_count: ageParts[0] || '', sapling_age_unit: ageParts[1] || 'Weeks',
      production_date: row.production_date ? new Date(row.production_date) : null,
      image_file: null, image_preview: imageUrl,
      latitude: row.latitude || '', longitude: row.longitude || '', address: row.address || 'N/A',
      is_fetching_location: false
    }]);
    setOpen(true);
  };

  const columns = [
    { field: 'lot_number', headerName: 'Lot No.', width: 100, cellClassName: 'no-wrap-cell' },
    { field: 'species_name', headerName: 'Species Name', width: 150, cellClassName: 'no-wrap-cell' },
    { field: 'saplings_available', headerName: 'Total Stock', width: 120, cellClassName: 'no-wrap-cell', render: (row) => (<Typography variant="body2" sx={{ fontWeight: 'bold', color: '#333', whiteSpace: 'nowrap' }}>{row.saplings_available}</Typography>) },
    { field: 'allocated_quantity', headerName: 'Allocated', width: 110, cellClassName: 'no-wrap-cell', render: (row) => (<Button variant="text" onClick={() => handleAllocatedClick(row)} sx={{ fontWeight: 'bold', color: '#ff9933', minWidth: 0, p: '2px 8px', whiteSpace: 'nowrap', '&:hover': { backgroundColor: '#fff3e0', textDecoration: 'underline' } }}>{row.allocated_quantity}</Button>) },
    { field: 'in_hand', headerName: 'In Hand', width: 100, cellClassName: 'no-wrap-cell', render: (row) => { const h = (Number(row.saplings_available) || 0) - (Number(row.allocated_quantity) || 0); return <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#006400', whiteSpace: 'nowrap' }}>{h}</Typography>; } },
    { field: 'total_selled', headerName: 'Total Sold', width: 100, cellClassName: 'no-wrap-cell', render: (row) => (<Typography variant="body2" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>{row.total_selled}</Typography>) },
    { field: 'total_selled_price', headerName: 'Sales Amount', width: 120, cellClassName: 'no-wrap-cell', render: (row) => (<Typography variant="body2" sx={{ color: 'green', fontWeight: 'bold', whiteSpace: 'nowrap' }}>₹{row.total_selled_price}</Typography>) },
    { field: 'sapling_age', headerName: 'Age', width: 100, cellClassName: 'no-wrap-cell' },
    { field: 'price_per_sapling', headerName: 'Price', width: 90, cellClassName: 'no-wrap-cell' },
    { field: 'address', headerName: 'Address', width: 200, cellClassName: 'address-cell', render: (row) => (<Tooltip title={row.address} arrow><Typography variant="body2" sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.3, fontSize: '12px' }}>{row.address}</Typography></Tooltip>) },
    { field: 'production_date', headerName: 'Prod. Date', width: 110, cellClassName: 'no-wrap-cell', render: (row) => (<Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>{row.production_date ? new Date(row.production_date).toLocaleDateString() : '-'}</Typography>) },
    {
      field: 'image', headerName: 'Image', width: 70, cellClassName: 'image-cell',
      render: (row) => {
        const img = row.image ? `/api/${row.image.replace(/\\/g, '/')}` : null;
        return row.image ? (
          <Box sx={{ position: 'relative', width: 50, height: 50, flexShrink: 0 }}>
            <Avatar src={img} variant="rounded" sx={{ width: 50, height: 50, border: '1px solid #ddd' }} />
            {row.latitude && row.longitude && (
              <Tooltip title={`Lat: ${row.latitude}, Lon: ${row.longitude}`}>
                <Box sx={{ position: 'absolute', bottom: 0, left: 0, width: '100%', bgcolor: 'rgba(0,0,0,0.6)', color: 'white', p: 0.2, fontSize: '7px', lineHeight: 1.1, display: 'flex', alignItems: 'center', justifyContent: 'center', borderBottomLeftRadius: '4px', borderBottomRightRadius: '4px', whiteSpace: 'nowrap' }}>
                  <LocationOn sx={{ fontSize: 6, mr: 0.3 }} />{Number(row.latitude).toFixed(4)}, {Number(row.longitude).toFixed(4)}
                </Box>
              </Tooltip>
            )}
          </Box>
        ) : (
          <Avatar variant="rounded" sx={{ width: 50, height: 50, bgcolor: '#eee', flexShrink: 0 }}><CameraAlt color="disabled" /></Avatar>
        );
      }
    }
  ];

  const goldButtonStyle = { bgcolor: '#0a6831', color: 'white', fontWeight: 'bold', '&:hover': { bgcolor: '#085a2a' } };
  const disabledDesktopButtonStyle = { 
    bgcolor: '#9e9e9e', 
    color: 'white', 
    fontWeight: 'bold', 
    cursor: 'not-allowed', 
    opacity: 0.7, 
    '&:hover': { bgcolor: '#9e9e9e' },
    '&.Mui-disabled': { 
      bgcolor: '#9e9e9e', 
      color: 'white', 
      opacity: 0.7,
      cursor: 'not-allowed'
    }
  };
  const getAvailableSpeciesOptions = () => { const ids = new Set(tableData.map(i => i.species_id)); return speciesOptions.filter(o => !ids.has(o.id)); };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <style>{tableStyles}</style>
      <Box p={{ xs: 1.5, sm: 3 }}>
        {!productionCenterId && <Alert severity="warning">No Production Center assigned</Alert>}
        
        <Box display="flex" justifyContent="space-between" mb={2} flexDirection={{ xs: 'column', sm: 'row' }} gap={1}>
          <Typography variant="h5" sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' } }}>Species Stock Management</Typography>
          
          <Box position="relative">
            <Button 
              variant="contained" 
              startIcon={<Add />} 
              onClick={() => { 
                // ✅ STRICT BLOCK: Always check before opening
                if (!IS_REAL_MOBILE_DEVICE) {
                  setDesktopBlockAlert(true);
                  return;
                }
                setEditMode(false); 
                setOpen(true); 
              }} 
              sx={isMobile ? goldButtonStyle : disabledDesktopButtonStyle}
              fullWidth={{ xs: true, sm: false }}
              disabled={!isMobile}
            >
              {isMobile ? "Add Species" : "Add Species"}
            </Button>
            {!isMobile && (
              <Tooltip 
                title={
                  <Box>
                    <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Block fontSize="small" /> Mobile Device Required
                    </Typography>
                    <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                      • Image capture with geo-tagging requires mobile camera
                    </Typography>
                    <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                      • Location access needed for address verification
                    </Typography>
                    <Typography variant="caption" sx={{ display: 'block' }}>
                      • Please use your mobile device to add species stock
                    </Typography>
                  </Box>
                }
                arrow
                placement="top"
              >
                <IconButton 
                  size="small" 
                  sx={{ 
                    position: 'absolute', 
                    top: -8, 
                    right: -8, 
                    bgcolor: '#ff5252', 
                    color: 'white',
                    width: 20,
                    height: 20,
                    '&:hover': { bgcolor: '#d32f2f' }
                  }}
                >
                  <Block sx={{ fontSize: 12 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>


        <Paper sx={{ overflowX: 'auto', '& .species-stock-table': { minWidth: 1300 }, '& table td:not(.address-cell)': { whiteSpace: 'nowrap !important' }, '& table th': { whiteSpace: 'nowrap !important' }, '& table td': { py: 1.5, px: 2 }, '& table th.MuiTableCell-head': { py: 1.5, px: 2, fontWeight: 600, backgroundColor: '#0a6831 !important', color: '#ffffff !important', borderBottom: '2px solid #085a2a', letterSpacing: '0.3px' }, '& table th.MuiTableCell-head span, & table th.MuiTableCell-head div, & table th.MuiTableCell-head p': { color: '#ffffff !important' }, '& table td.address-cell': { whiteSpace: 'normal !important', maxWidth: 200, minWidth: 150 }, '& table tbody tr:hover': { backgroundColor: '#f0f7f2 !important' }, '& table tbody tr:nth-of-type(even)': { backgroundColor: '#fafafa' }, '& table tbody tr:nth-of-type(even):hover': { backgroundColor: '#f0f7f2 !important' } }}>
          {initialLoading ? (
            <Box textAlign="center" p={5}><CircularProgress /></Box>
          ) : (
            <DynamicTable data={tableData} columns={columns} loading={loading} onEdit={handleEdit} onDelete={handleDeleteClick} className="species-stock-table" />
          )}
        </Paper>

        <DynamicOffcanvas 
          open={open} 
          onClose={() => setOpen(false)} 
          title={editMode ? "Edit Species Stock" : "Add Species Stock"} 
          width={{ xs: '100%', sm: 500 }}
          actions={
            <>
              {!editMode && (
                <Button 
                  onClick={addRow} 
                  fullWidth 
                  variant="outlined" 
                  disabled={!IS_REAL_MOBILE_DEVICE}
                  sx={{ 
                    mb: 2, 
                    color: IS_REAL_MOBILE_DEVICE ? '#0a6831' : '#9e9e9e', 
                    borderColor: IS_REAL_MOBILE_DEVICE ? '#0a6831' : '#9e9e9e',
                    '&:hover': { 
                      borderColor: IS_REAL_MOBILE_DEVICE ? '#085a2a' : '#9e9e9e', 
                      backgroundColor: IS_REAL_MOBILE_DEVICE ? '#f0f7f2' : 'transparent' 
                    },
                    cursor: IS_REAL_MOBILE_DEVICE ? 'pointer' : 'not-allowed'
                  }}
                >
                  + Add Another Row
                </Button>
              )}
              <Button onClick={handleSubmit} startIcon={<Save />} fullWidth variant="contained" sx={goldButtonStyle} disabled={submitting}>
                {submitting ? "Saving..." : "Save Changes"}
              </Button>
            </>
          }
        >
          <Box mt={1}>
            {items.map((item, i) => (
              <Box key={item.id} mb={3} p={{ xs: 1.5, sm: 2 }} border="1px solid #eee" borderRadius="8px">
                <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                  
                  {editMode ? (
                    <Grid item xs={12}>
                      <DynamicField field={{ name: 'species_name', label: 'Species Name' }} value={item.species_name} readOnly />
                    </Grid>
                  ) : (
                    <Grid item xs={12} sx={{width:'100%'}}>
                      <Autocomplete 
                        sx={{ width:"100%"}}
                        options={getAvailableSpeciesOptions()} 
                        getOptionLabel={(o) => `${o.name || ''} ${o.name_tamil ? `(${o.name_tamil})` : ''}`} 
                        value={speciesOptions.find(o => o.id === item.species_id) || null} 
                        onChange={(e, v) => handleSpeciesChange(i, v)}
                        renderInput={(p) => (
                          <TextField 
                            {...p} 
                            label="Select Species" 
                            size="small" 
                            required 
                            error={!!errors[i]?.species_id} 
                            helperText={errors[i]?.species_id} 
                            sx={greenInputSx}
                          />
                        )}
                      />
                    </Grid>
                  )}

                  <Grid item xs={12} sx={{width:"100%"}}>
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                      Geo-Tag Verification {editMode && "(Optional: Leave to keep current)"}
                    </Typography>
                    <Box display="flex" alignItems="flex-start" gap={2} flexDirection={{ xs: 'column', sm: 'row' }}>
                      <Box sx={{ position: 'relative', alignSelf: { xs: 'center', sm: 'flex-start' } }}>
                        {!editMode && !IS_REAL_MOBILE_DEVICE ? (
                          <Box 
                            sx={{ 
                              height: 100, width: 100, border: '2px dashed #ccc', 
                              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                              bgcolor: '#f5f5f5', borderRadius: 1, opacity: 0.5
                            }}
                          >
                            <Block sx={{ fontSize: 30, color: '#ccc' }} />
                            <Typography variant="caption" sx={{ mt: 1, fontSize: '9px', color: '#999', textAlign: 'center', px: 0.5 }}>Blocked</Typography>
                          </Box>
                        ) : (
                          <Button 
                            variant="outlined" 
                            component="label" 
                            disabled={!IS_REAL_MOBILE_DEVICE && !editMode}
                            sx={{ 
                              height: 100, width: 100, border: '2px dashed #ccc', p: 0, overflow: 'hidden', 
                              '&:hover': { borderColor: (IS_REAL_MOBILE_DEVICE || editMode) ? '#0a6831' : '#ccc' }, 
                              bgcolor: (IS_REAL_MOBILE_DEVICE || editMode) ? 'transparent' : '#f5f5f5', 
                              cursor: (IS_REAL_MOBILE_DEVICE || editMode) ? 'pointer' : 'not-allowed' 
                            }}
                          >
                            {item.image_preview ? (
                              <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
                                <img src={item.image_preview} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                {item.latitude && item.longitude && (
                                  <Box sx={{ position: 'absolute', bottom: 0, left: 0, width: '100%', bgcolor: 'rgba(0,0,0,0.6)', color: 'white', p: 0.5, fontSize: '9px', lineHeight: 1.2, display: 'flex', alignItems: 'center', justifyContent: 'center', whiteSpace: 'nowrap' }}>
                                    <LocationOn sx={{ fontSize: 8, mr: 0.5 }} />
                                    {Number(item.latitude).toFixed(5)}, {Number(item.longitude).toFixed(5)}
                                  </Box>
                                )}
                              </Box>
                            ) : (
                              <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="100%">
                                <CameraAlt sx={{ fontSize: 30, color: (IS_REAL_MOBILE_DEVICE || editMode) ? '#aaa' : '#ccc' }} />
                                <Typography variant="caption" sx={{ mt: 1, fontSize: '10px', color: 'textSecondary', textAlign: 'center', px: 1 }}>
                                  {editMode ? 'Update Photo' : 'Capture Photo'}
                                </Typography>
                              </Box>
                            )}
                            {(IS_REAL_MOBILE_DEVICE || editMode) && (
                              <input type="file" hidden accept="image/*" capture="environment" onChange={(e) => handleImageCapture(i, e)} />
                            )}
                          </Button>
                        )}
                        {item.is_fetching_location && <CircularProgress size={24} sx={{ position: 'absolute', top: '50%', left: '50%', mt: '-12px', ml: '-12px' }} />}
                      </Box>
                      
                      <Box sx={{width: "100%"}}>
                        <TextField 
                          sx={[greenInputSx,{width: '100%'}]}
                          fullWidth size="small" label="Captured Address" multiline rows={3} 
                          value={item.address} readOnly placeholder="Fetching location..."
                          InputProps={{
                            endAdornment: item.latitude && item.longitude && (
                              <Tooltip title={`Lat: ${item.latitude}, Lon: ${item.longitude}`}>
                                <LocationOn color="primary" fontSize="small" sx={{ cursor: 'pointer', alignSelf: 'flex-start', mt: 1 }} />
                              </Tooltip>
                            ),
                            startAdornment: (
                              <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                                <IconButton 
                                  size="small" 
                                  onClick={() => fetchLocationAndAddress(i)} 
                                  disabled={item.is_fetching_location || (!IS_REAL_MOBILE_DEVICE && !editMode)}
                                >
                                  <MyLocation fontSize="small" />
                                </IconButton>
                              </InputAdornment>
                            )
                          }}
                        />
                        {errors[i]?.image_file && (
                          <Typography variant="caption" color="error" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                            <Block sx={{ fontSize: 14 }} />
                            {errors[i].image_file} - Mobile device required
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={12} sx={{width:"100%"}}>
                    <DynamicField sx={{width:"100%"}} field={{ name: 'saplings_available', label: 'Total Stock', required: true, type: 'number' }} value={item.saplings_available} onChange={(e) => handleChange(i, e)} error={!!errors[i]?.saplings_available} helperText={errors[i]?.saplings_available} />
                  </Grid>
                  <Grid item xs={12} sm={6} sx={{width:"100%"}}>
                    <DynamicField sx={{width:"100%"}} field={{ name: 'price_per_sapling', label: 'Price per unit', required: true, type: 'number' }} value={item.price_per_sapling} onChange={(e) => handleChange(i, e)} error={!!errors[i]?.price_per_sapling} helperText={errors[i]?.price_per_sapling} />
                  </Grid>
                  <Grid item xs={12} sm={6} sx={{width:"100%"}}>
                    <DatePicker
                      label="Production Date"
                      value={item.production_date}
                      onChange={(v) => handleDateChange(i, v)}
                      slotProps={{
                        textField: { size: 'small', fullWidth: true, required: !editMode, error: !!errors[i]?.production_date, helperText: errors[i]?.production_date, sx: greenInputSx }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField sx={greenInputSx} label="Age Count" name="sapling_age_count" type="number" size="small" fullWidth value={item.sapling_age_count} onChange={(e) => handleChange(i, e)} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField sx={greenInputSx} select label="Age Unit" name="sapling_age_unit" size="small" fullWidth value={item.sapling_age_unit} onChange={(e) => handleChange(i, e)}>
                      <MenuItem value="Days">Days</MenuItem>
                      <MenuItem value="Weeks">Weeks</MenuItem>
                      <MenuItem value="Months">Months</MenuItem>
                      <MenuItem value="Years">Years</MenuItem>
                    </TextField>
                  </Grid>
                </Grid>
              </Box>
            ))}
          </Box>
        </DynamicOffcanvas>

        {/* Desktop Block Dialog */}
        <Dialog open={desktopBlockAlert} onClose={() => setDesktopBlockAlert(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
          <Box sx={{ bgcolor: '#fff3e0', p: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ width: 60, height: 60, borderRadius: '50%', bgcolor: '#ff9800', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <DesktopWindows sx={{ fontSize: 32, color: 'white' }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight="bold" color="#e65100">Desktop Device Detected</Typography>
              <Typography variant="body2" color="#bf360c">This action is NOT available on desktop</Typography>
            </Box>
          </Box>
          <DialogContent sx={{ pt: 3 }}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body1" gutterBottom fontWeight="bold">Adding new species stock requires:</Typography>
              <Box component="ul" sx={{ pl: 2, m: 0 }}>
                <Box component="li" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CameraAlt sx={{ fontSize: 18, color: '#0a6831' }} />
                  <Typography variant="body2">Camera access for capturing sapling photos</Typography>
                </Box>
                <Box component="li" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LocationOn sx={{ fontSize: 18, color: '#0a6831' }} />
                  <Typography variant="body2">GPS location for geo-tagging images</Typography>
                </Box>
                <Box component="li" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PhoneAndroid sx={{ fontSize: 18, color: '#0a6831' }} />
                  <Typography variant="body2">Native device features for verification</Typography>
                </Box>
              </Box>
            </Box>
            
            <Alert severity="error" sx={{ borderRadius: 2, mb: 2 }}>
              <Typography variant="body2" fontWeight="bold">
                ⚠️ Resizing your browser window will NOT bypass this restriction.
              </Typography>
              <Typography variant="caption" display="block">
                Device detection is based on hardware identity, not screen size.
              </Typography>
            </Alert>

            <Alert severity="info" sx={{ borderRadius: 2 }}>
              <Typography variant="body2">
                <strong>What you can do on desktop:</strong> Edit existing species stock details, delete entries, and view allocation reports.
              </Typography>
            </Alert>
          </DialogContent>
          <DialogActions sx={{ p: 3, gap: 1 }}>
            <Button onClick={() => setDesktopBlockAlert(false)} variant="contained" sx={{ bgcolor: '#0a6831', '&:hover': { bgcolor: '#085a2a' }, fontWeight: 'bold' }}>
              Understood
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Dialog */}
        <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} fullWidth maxWidth="xs">
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent><Typography>Are you sure you want to delete <strong>{selectedRow?.species_name}</strong>?</Typography></DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
            <Button onClick={confirmDelete} color="error" variant="contained" startIcon={<DeleteIcon />}>Delete</Button>
          </DialogActions>
        </Dialog>

        {/* Allocation Modal */}
        <Dialog open={allocationModalOpen} onClose={() => setAllocationModalOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>
            Allocation Details: <b>{selectedStockInfo?.species_name}</b>
            <Typography variant="caption" display="block" color="textSecondary">Stock ID: {selectedStockInfo?.id}</Typography>
          </DialogTitle>
          <DialogContent dividers>
            {allocationLoading ? (
              <Box display="flex" justifyContent="center" p={3}><CircularProgress /></Box>
            ) : allocationData.length > 0 ? (
              <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ backgroundColor: '#0a6831', color: '#ffffff', fontWeight: 600, whiteSpace: 'nowrap' }}>Req ID</TableCell>
                      <TableCell sx={{ backgroundColor: '#0a6831', color: '#ffffff', fontWeight: 600, whiteSpace: 'nowrap' }}>Farmer ID</TableCell>
                      <TableCell sx={{ backgroundColor: '#0a6831', color: '#ffffff', fontWeight: 600, whiteSpace: 'nowrap' }}>Species</TableCell>
                      <TableCell align="right" sx={{ backgroundColor: '#0a6831', color: '#ffffff', fontWeight: 600, whiteSpace: 'nowrap' }}>Req Qty</TableCell>
                      <TableCell align="right" sx={{ backgroundColor: '#0a6831', color: '#ffffff', fontWeight: 600, whiteSpace: 'nowrap' }}>App Qty</TableCell>
                      <TableCell sx={{ backgroundColor: '#0a6831', color: '#ffffff', fontWeight: 600, whiteSpace: 'nowrap' }}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {allocationData.map((it, idx) => (
                      <TableRow key={idx} sx={{ '&:nth-of-type(even)': { backgroundColor: '#fafafa' }, '&:hover': { backgroundColor: '#f0f7f2' } }}>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{it.request_id || it.id}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{it.farmer_id || 'N/A'}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{it.species_name || 'N/A'}</TableCell>
                        <TableCell align="right" sx={{ color: 'green', fontWeight: 'bold', whiteSpace: 'nowrap' }}>{it.requested_quantity}</TableCell>
                        <TableCell align="right" sx={{ color: 'green', fontWeight: 'bold', whiteSpace: 'nowrap' }}>{it.approved_quantity}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{it.status || 'N/A'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Alert severity="info">No allocation details found.</Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAllocationModalOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default SpeciesStock;